/**
 * Page Layout Configuration
 * 
 * This configuration defines the structure of dynamic pages (e.g., Landing/Home).
 * Each section has a type and optional props.
 * 
 * In the future, this should be fetched from:
 * GET /api/v2/sites/layout/
 */

export const defaultPageLayout = {
  sections: [
    {
      id: "hero",
      type: "hero-carousel",
      enabled: true,
      _order: 1,
      props: {
        autoPlay: true,
        interval: 5000,
      },
    },
    {
      id: "intro",
      type: "store-intro",
      enabled: true,
      _order: 2,
      props: {
        showAddress: true,
        showContact: true,
      },
    },
    {
      id: "notices",
      type: "notice-list",
      enabled: true,
      _order: 3,
      props: {
        limit: 5,
        showViewAll: true,
      },
    },
    {
      id: "coupons",
      type: "coupon-section",
      enabled: false,
      _order: 4,
      props: {
        limit: 4,
      },
    },
    {
      id: "group-reservation",
      type: "group-reservation-link",
      enabled: false,
      _order: 5,
      props: {},
    },
    {
      id: "display-menu",
      type: "display-menu",
      enabled: true,
      _order: 6,
      props: {
        limit: 6,
      },
    },
    {
      id: "facilities",
      type: "facility-grid",
      enabled: true,
      _order: 7,
      props: {
        columns: 2,
      },
    },
    {
      id: "sns",
      type: "sns-links",
      enabled: true,
      _order: 8,
      props: {},
    },
    {
      id: "reviews",
      type: "review-carousel",
      enabled: false,
      _order: 9,
      props: {
        limit: 10,
      },
    },
    {
      id: "gallery",
      type: "carousel-gallery",
      enabled: true,
      _order: 10,
      props: {
        autoPlay: false,
        interval: 7000,
      },
    },
    {
      id: "map",
      type: "map",
      enabled: true,
      _order: 11,
      props: {},
    },
  ],
};

/**
 * Get the page layout for a given page type.
 * 
 * @param {string} pageType - The page type (e.g., "landing", "home")
 * @param {object} siteDetail - The site detail object containing visibility settings
 * @returns {Promise<object>} The page layout configuration
 */
export async function getPageLayout(pageType = "landing", siteDetail = null) {
  // Clone the default config
  const config = JSON.parse(JSON.stringify(defaultPageLayout));

  if (siteDetail?.section_visibility) {
    const visibility = siteDetail.section_visibility;

    // Sort sections by order property and apply visibility filtering
    config.sections = config.sections
      .map(section => {
        const visibilityKey = {
          'hero-carousel': 'hero_carousel',
          'store-intro': 'business_intro',
          'notice-list': 'notice',
          'coupon-section': 'coupon',
          'group-reservation-link': 'group_reservation',
          'display-menu': 'display_menu',
          'facility-grid': 'facilities',
          'sns-links': 'sns_links',
          'review-carousel': 'reviews',
          'carousel-gallery': 'carousel_gallery',
          'map': 'map',
        }[section.type];

        // Handle visibility data - support both old boolean format and new {enabled, order} format
        if (visibilityKey && visibility[visibilityKey] !== undefined) {
          const visData = visibility[visibilityKey];
          
          // If it's a boolean (old format), treat as enabled with no order override
          if (typeof visData === 'boolean') {
            return {
              ...section,
              enabled: visData
            };
          }
          
          // If it's an object (new format with enabled and order)
          if (typeof visData === 'object' && visData !== null) {
            return {
              ...section,
              enabled: visData.enabled ?? true,
              _order: visData.order ?? section._order ?? 99
            };
          }
        }
        return section;
      })
      .sort((a, b) => {
        // Sort by order property if available
        const orderA = a._order ?? 99;
        const orderB = b._order ?? 99;
        return orderA - orderB;
      })
      .map(({ _order, ...section }) => section); // Remove temporary _order property
  }

  return Promise.resolve(config);
}

export default defaultPageLayout;
